import React from 'react';
import { Table, Card, Tag, Tooltip, Space, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLogs } from '../../store/actions/analytics';

const ActivityFeed = ({ logs, loading, pagination }) => {
  const dispatch = useDispatch();
  const { filters, users } = useSelector(state => state.analytics);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : `User ${userId}`;
  };

  const isGrouped = logs && logs.length > 0 && logs[0].hasOwnProperty('count');

  const statusColors = {
    SUCCESS: 'success',
    FAILURE: 'error',
    INFO: 'blue',
    WARNING: 'warning',
  };

  const defaultColumns = [
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'User',
      dataIndex: 'user_id',
      key: 'user_id',
      render: (userId, record) => {
        // Use the new user_name field if available
        const userName = record.user_name || getUserName(userId);
        return (
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            {userName}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={statusColors[status]}>{status}</Tag>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (timestamp) => new Date(timestamp).toLocaleString(),
    },
  ];

  const groupedColumns = [
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
      width: 80,
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 100,
      render: (status) => <Tag color={statusColors[status]}>{status}</Tag>,
    },
    {
      title: 'Users',
      dataIndex: 'users',
      key: 'users',
      width: 250,
      render: (users, record) => {
        // NEW: Use the users array if available (from improved API)
        if (users && Array.isArray(users) && users.length > 0) {
          const displayUsers = users.slice(0, 3);
          const remaining = users.length - 3;
          
          return (
            <Space size={4} wrap>
              {displayUsers.map(user => (
                <Tag key={user.id} icon={<UserOutlined />}>
                  {user.name}
                </Tag>
              ))}
              {remaining > 0 && (
                <Tooltip 
                  title={users.slice(3).map(u => u.name).join(', ')}
                  placement="topLeft"
                >
                  <Tag>+{remaining} more</Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        
        // FALLBACK: Use user_names string if users array not available
        if (record.user_names) {
          const names = record.user_names.split(', ');
          const displayNames = names.slice(0, 3);
          const remaining = names.length - 3;
          
          return (
            <Space size={4} wrap>
              {displayNames.map((name, idx) => (
                <Tag key={idx} icon={<UserOutlined />}>
                  {name}
                </Tag>
              ))}
              {remaining > 0 && (
                <Tooltip 
                  title={names.slice(3).join(', ')}
                  placement="topLeft"
                >
                  <Tag>+{remaining} more</Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        
        // LEGACY FALLBACK: Parse user_ids and lookup names
        if (record.user_ids) {
          const userIds = record.user_ids.split(',');
          return (
            <Tooltip title={`${userIds.length} user(s): ${userIds.join(', ')}`}>
              <Tag icon={<UserOutlined />}>
                {userIds.length} user(s)
              </Tag>
            </Tooltip>
          );
        }
        
        return '-';
      },
    },
    {
      title: 'Last Occurred',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (timestamp) => new Date(timestamp).toLocaleString(),
    },
  ];

  const columns = isGrouped ? groupedColumns : defaultColumns;

  const handleTableChange = (pagination) => {
    dispatch(fetchLogs(pagination.current, pagination.pageSize, filters));
  };

  return (
    <Table
      dataSource={logs}
      columns={columns}
      loading={loading}
      pagination={{
        current: pagination.currentPage,
        pageSize: pagination.perPage,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} items`,
      }}
      onChange={handleTableChange}
      rowKey={isGrouped ? (record) => `${record.message}-${record.status}` : "id"}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default ActivityFeed;